import type { ApiReturnSiteIdsToDashboardIds } from '@highimpact-frontend/api-types';
import { useCallback, useEffect, useState } from 'react';

import { useDashboardHost } from '../context/DashboardHostContext';

// -- type definitions --

export interface UseSiteDashboardIndexResult {
  readonly data: ApiReturnSiteIdsToDashboardIds | null;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly reload: () => Promise<void>;
}

// -- main hook --

/**
 * Loads the site-to-dashboard index from the host API instead of Redux externals.
 */
export function useSiteDashboardIndex(): UseSiteDashboardIndexResult {
  const host = useDashboardHost();
  const [data, setData] = useState<ApiReturnSiteIdsToDashboardIds | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const siteDashboardMap =
        await host.api.dashboard.getSiteIdsToDashboardIds();
      setData(siteDashboardMap);
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError : new Error(String(loadError))
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [host.api.dashboard]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, loading, error, reload };
}
